import {
  DocumentTranscriptUtteranceV1,
  DocumentTranscriptParagraphV1
} from '@utils/types/editors/transcripts/transcript.ts';

/** Flatten a transcript utterance into a plain text preview.
 *
 * Utterance shape is `utterance → paragraphs → text runs` — the editor writes
 * a text.text on every leaf. Joining them with spaces mirrors the ``_utt_text``
 * helper on the API side so the preview here is a faithful rendering of what
 * the backend will write into the VTT cue. */
export const utteranceText = (utt: DocumentTranscriptUtteranceV1): string => {
  const parts: string[] = [];
  for (const paragraph of utt.children as DocumentTranscriptParagraphV1[]) {
    for (const t of paragraph.children) {
      if (t.text) parts.push(t.text);
    }
  }
  return parts.join(' ').trim();
};

/** Utterance timing may be authored on the utterance itself, on a child
 *  paragraph, or (in older exports) only on the leaf text nodes. Walk the
 *  same fallback the backend uses so this panel never shows a zero-length
 *  window for a doc the server would happily accept. */
export const utteranceStart = (utt: DocumentTranscriptUtteranceV1): number => {
  if (utt.start != null) return utt.start;
  for (const p of utt.children as DocumentTranscriptParagraphV1[]) {
    if (p.start != null) return p.start;
    for (const t of p.children) if (t.start != null) return t.start;
  }
  return 0;
};

export const utteranceStop = (utt: DocumentTranscriptUtteranceV1): number => {
  if (utt.stop != null) return utt.stop;
  const children = utt.children as DocumentTranscriptParagraphV1[];
  for (let i = children.length - 1; i >= 0; i--) {
    const p = children[i];
    if (p.stop != null) return p.stop;
    for (let j = p.children.length - 1; j >= 0; j--) {
      const t = p.children[j];
      if (t.stop != null) return t.stop;
    }
  }
  return 0;
};
